/**
 * @fileoverview Componente Molecule para mostrar y alternar el popover de Mia en el módulo de Inbox.
 * Permite gestionar el estado de visibilidad del popover y su interacción.
 *
 * @module ToggleMiaPopoverMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.1.0
 */
import React, { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ButtonComponent from '../../../components/ButtonMC';

/**
 * @typedef {Object} ToggleMiaPopoverMCProps
 * @description Propiedades para el componente ToggleMiaPopoverMC.
 * @property {boolean} isOpen - Indica si el popover está abierto.
 * @property {function} onToggle - Función para alternar el estado del popover.
 * @property {ReactNode} [children] - Elementos hijos a renderizar dentro del popover.
 */

/**
 * Componente Molecule para alternar el popover de Mia.
 *
 * Implementa el patrón de Molecule con separación de responsabilidades:
 * - Gestión de estado de visibilidad del popover
 * - Renderizado condicional del contenido
 * - Integración con UI global
 *
 * **Optimización de rendimiento:**
 * - Uso de React.memo para evitar renders innecesarios
 * - Uso de useCallback para handlers
 *
 * @component ToggleMiaPopoverMC
 * @category Component - Molecule
 * @param {ToggleMiaPopoverMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado
 *
 * @example
 * <ToggleMiaPopoverMC isOpen={true} onToggle={handleToggle}>
 *   <div>Contenido del popover</div>
 * </ToggleMiaPopoverMC>
 *
 * @see ../README.md Documentación general del módulo Inbox
 * @see ../hooks.md Documentación de hooks relacionados
 *
 * @since 2.0.0
 * @version 3.1.0 - Optimización de memoización y separación de lógica
 */
const ToggleMiaPopoverMC = memo(({ tooltipId, onDeactivate, onClose }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(true);

  const onCloseHandler = () => {
    setShow(false);
    if (onClose) {
      onClose();
    }
  };

  const onDeactivateHandler = () => {
    onDeactivate();
    onCloseHandler();
  };

  return (
    <ReactTooltip
      anchorSelect={`#${tooltipId}`}
      place="top"
      clickable
      positionStrategy="fixed"
      className="tw-z-[100] tw-bg-white tw-rounded-[12px] tw-text-black tw-border tw-border-[#E4E4E4] tw-text-[14px]"
      isOpen={show}
      backgroundColor="#fff"
      opacity={1}
    >
      <div className="tw-wrapper tw-py-6" style={{ width: '390px' }}>
        <p className="tw-mb-1 tw-font-medium tw-text-gray-900 tw-text-sm">
          {t('chatbot_ai.general.toggle_popover.title')}
        </p>
        <p className="tw-mb-3 tw-font-normal tw-text-gray-900 tw-text-sm">
          <Trans i18nKey="chatbot_ai.general.toggle_popover.body" components={{ strong: <strong className="tw-font-semibold" /> }} />
        </p>
        <div className="tw-flex tw-mt-2">
          <ButtonComponent
            className="tw-py-3 tw-px-5"
            text={t('chatbot_ai.general.toggle_popover.deactivate_mia')}
            onClick={onDeactivateHandler}
          />
          <ButtonComponent
            variant="SECONDARY"
            className="tw-ml-2 tw-py-3 tw-px-5 !tw-border !tw-border-blue-500"
            text={t('chatbot_ai.general.toggle_popover.understood')}
            onClick={onCloseHandler}
          />
        </div>
      </div>
    </ReactTooltip>
  );
});

ToggleMiaPopoverMC.displayName = 'ToggleMiaPopoverMC';

export default ToggleMiaPopoverMC;
